import { useCallback, useEffect, useRef, useState } from "react"

import mapboxgl from "mapbox-gl"

export function getAccessKey() {
  return process.env.REACT_APP_MAP_ACCESS_KEY
}

export function initMap() {
  mapboxgl.accessToken = getAccessKey()
}

export const MapStyles = {
  DEFAULT: "mapbox://styles/mapbox/standard",
  STREETS: "mapbox://styles/mapbox/streets-v12",
  OUTDOORS: "mapbox://styles/mapbox/outdoors-v12",
  LIGHT: "mapbox://styles/mapbox/light-v11",
  DARK: "mapbox://styles/mapbox/dark-v11",
  SATELLITE: "mapbox://styles/mapbox/satellite-v9",
  SATELLITE_STREETS: "mapbox://styles/mapbox/satellite-streets-v12",
  DAY: "mapbox://styles/mapbox/navigation-day-v1",
  NIGHT: "mapbox://styles/mapbox/navigation-night-v1",
}

const DEFAULTS = {
  style: MapStyles.LIGHT,
}

export function useMap(defaults) {
  const cleanDefaults = {
    ...DEFAULTS,
    ...(defaults || {}),
  }

  const mapRef = useRef(null)
  const defaultRef = useRef(cleanDefaults)

  const [latitude, setLatitude] = useState(() => cleanDefaults.latitude)
  const [longitude, setLongitude] = useState(() => cleanDefaults.longitude)
  const [zoom, setZoom] = useState(() => cleanDefaults.zoom)
  const [style] = useState(() => cleanDefaults.style)
  const [boundingBox, setBoundingBox] = useState(() => cleanDefaults.boundingBox)
  const [isLoading, setIsLoading] = useState(true)

  const setContainer = useCallback((ref, options) => {
    if (mapRef.current != null) {
      return
    }

    const newLatitude = options?.latitude ?? defaultRef.current.latitude
    const newLongitude = options?.longitude ?? defaultRef.current.longitude
    const newZoom = options?.zone ?? defaultRef.current.zoom
    const newBoundingBox = options?.boundingBox ?? defaultRef.current.boundingBox

    const mapArgs = {}
    if (newBoundingBox) {
      mapArgs.bounds = newBoundingBox
      if (options?.fitBoundsOptions) {
        mapArgs.fitBoundsOptions = options.fitBoundsOptions
      }
    } else if (newLongitude != null && newLatitude != null && newZoom != null) {
      mapArgs.center = [newLongitude, newLatitude]
      mapArgs.zoom = newZoom
    }

    mapRef.current = new mapboxgl.Map({
      container: ref.current,
      style: options?.style ?? defaultRef.current.style,
      ...mapArgs,
    })

    mapRef.current.on("style.load", () => {
      setIsLoading(() => false)
    })
  }, [])

  useEffect(() => {
    if (isLoading) {
      return
    }

    const getInfo = () => {
      const center = mapRef.current.getCenter()

      const currentLatitude = center.lat
      const currentLongitude = center.lng
      const currentZoom = mapRef.current.getZoom()
      const bounds = mapRef.current.getBounds()
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      const boundingBox = [sw.lng, sw.lat, ne.lng, ne.lat]

      const changedLocation = latitude !== currentLatitude || longitude !== currentLongitude
      const changedZoom = zoom !== currentZoom

      return {
        latitude: currentLatitude,
        longitude: currentLongitude,
        zoom: currentZoom,
        boundingBox,
        hasLocationChange: changedLocation,
        hasZoomChange: changedZoom,
        hasChange: changedLocation || changedZoom,
      }
    }

    const handleChange = () => {
      const info = getInfo()
      if (info.hasLocationChange) {
        setLatitude(() => info.latitude)
        setLongitude(() => info.longitude)
      }

      if (info.hasZoomChange) {
        setZoom(() => info.zoom)
      }

      if (info.hasChange) {
        setBoundingBox(() => info.boundingBox)
      }
    }

    // mapRef.current.on("boxzoomend", handleChange)
    // mapRef.current.on("wheel", handleChange)
    mapRef.current.on("moveend", handleChange)

    return () => {
      // mapRef.current.off("boxzoomend", handleChange)
      // mapRef.current.off("wheel", handleChange)
      mapRef.current.off("moveend", handleChange)
    }
  }, [latitude, longitude, zoom, style, isLoading])

  return {
    map: mapRef.current,

    isLoading,
    setContainer,

    boundingBox,
    latitude,
    longitude,
    zoom,

    setLocation: useCallback((longitude, latitude) => {
      setLatitude(() => latitude)
      setLongitude(() => longitude)

      if (mapRef.current == null) {
        return
      }

      mapRef.current.setCenter([longitude, latitude])
    }, []),
    setZoom: useCallback(zoom => {
      setZoom(() => zoom)

      if (mapRef.current == null) {
        return
      }

      mapRef.current.setZoom(zoom)
    }, []),
    setBoundingBox: useCallback(
      (newBoundingBox, options) => {
        if (!newBoundingBox || boundingBox === newBoundingBox) {
          return
        }

        setBoundingBox(() => newBoundingBox)

        if (mapRef.current == null) {
          return
        }

        mapRef.current.fitBounds(newBoundingBox, options)
      },
      [boundingBox]
    ),
  }
}
